// SchedulePages.tsx — /liff/manager/schedule + /liff/officer/schedule + /liff/officer/history
//
// schedule-api / patrol-api endpoints for these aren't yet GA, so the pages
// render the operational layout (week strip, day rows, etc.) plus a
// ComingSoonCard with a clear list of what each will provide. The week
// strip itself is real — it computes Monday-anchored ISO weeks in the
// user's local time and lets them flip back-and-forth.

import React, { useEffect, useMemo, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import {
  fetchMySchedule, createSwapRequest,
  type MyScheduleData, type MyScheduleEntry, type MyScheduleException,
} from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, ComingSoonCard, ListRow, EmptyMobile, PrimaryButton,
  MobileSpinner,
} from "../../../liff/components/Mobile.js";
import { apiFetch } from "../../../lib/api-client.js";
import { buildDashboardUrl } from "@nexus/shared/links";
import MySubmissions from "../components/MySubmissions.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

const KIND_LABEL: Record<MyScheduleException["kind"], string> = {
  holiday: "假日", typhoon: "颱風", closure: "案場關閉", extra_coverage: "加開", custom: "其他",
};
const KIND_TONE: Record<MyScheduleException["kind"], string> = {
  holiday: "bg-danger/15 text-danger",
  typhoon: "bg-warning/15 text-warning",
  closure: "bg-text-muted/15 text-text-secondary",
  extra_coverage: "bg-accent/15 text-accent",
  custom: "bg-bg-raised text-text-secondary",
};

function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function WeekStrip({ anchor, onSelect }: { anchor: Date; onSelect: (d: Date) => void }): React.ReactElement {
  const days = useMemo(() => {
    const d0 = new Date(anchor);
    const dow = (d0.getDay() + 6) % 7; // Mon=0..Sun=6
    d0.setDate(d0.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(d0);
      d.setDate(d0.getDate() + i);
      return d;
    });
  }, [anchor]);

  const today = new Date();
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {days.map((d) => {
        const isToday = d.toDateString() === today.toDateString();
        const isSelected = d.toDateString() === anchor.toDateString();
        return (
          <button
            key={d.toISOString()}
            onClick={() => onSelect(d)}
            className={`flex flex-col items-center py-2 rounded-lg ${
              isSelected ? "bg-accent text-text-inverse"
                : isToday ? "bg-bg-raised text-accent"
                : "bg-bg-raised text-text-secondary"
            }`}
          >
            <span className="text-[10px] uppercase tracking-wider opacity-70">
              {["一", "二", "三", "四", "五", "六", "日"][(d.getDay() + 6) % 7]}
            </span>
            <span className="text-base font-semibold">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * /liff/manager/schedule — redirect-only stub.
 *
 * 月排班 / 班別 / 換班單 / 月時數的完整 dashboard 已上線（PR #151
 * Phase 1A~1K），手機螢幕做拖曳式排班 UX 太差，所以本頁不再嘗試在
 * LIFF 內 reproduce — 一進來就把使用者送去 dashboard /dashboard/schedules。
 *
 * 同 domain (nexus.sentry.red)，nexus_session cookie 在 LINE in-app
 * browser 與外部 Safari/Chrome 共享，所以使用者通常無感切過去；若沒登
 * 入 dashboard 端會自動引導 /login。
 *
 * 未來移除此 LIFF id：把 endpoints.json 的 manager_schedule entry 拿掉
 * 然後跑 ensure-all-liff.mjs workflow；rich-menu 對應 cell 改連
 * https://nexus.sentry.red/dashboard/schedules（regular URL action，
 * 非 LIFF）。在此之前留 redirect stub。
 */
export function ManagerSchedulePage(): React.ReactElement {
  useEffect(() => {
    const t = setTimeout(() => {
      window.location.replace(buildDashboardUrl("/dashboard/schedules"));
    }, 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <MobileShell title="班表編排" subtitle="正在開啟完整版…">
      <Card>
        <p className="text-sm text-text-secondary leading-relaxed">
          班表編排已升級為完整版（月曆、自動生成、換班審核、月時數匯出）。
          手機螢幕僅作於檢視，編輯請改用 dashboard。
        </p>
        <p className="mt-3 text-xs text-text-muted">如果沒有自動跳轉，請點下方按鈕。</p>
        <div className="h-3" />
        <PrimaryButton href={buildDashboardUrl("/dashboard/schedules")}>
          前往班表 Dashboard
        </PrimaryButton>
      </Card>
    </MobileShell>
  );
}

/** /liff/officer/schedule — officer's own schedule. */
export function OfficerSchedulePage(): React.ReactElement {
  const { session } = useLiff();
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState<MyScheduleData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [swapModal, setSwapModal] = useState<MyScheduleEntry | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!session?.session_token) return;
    const tok = session.session_token;
    let cancelled = false;
    (async () => {
      const today = new Date();
      const from = ymdLocal(today);
      const to = ymdLocal(new Date(today.getTime() + 14 * 86400_000));
      try {
        const r = await fetchMySchedule(tok, from, to);
        if (!cancelled) setData(r);
      } catch (e) { if (!cancelled) setErr(e instanceof Error ? e.message : "載入失敗"); }
    })();
    return () => { cancelled = true; };
  }, [session?.session_token, reload]);

  const anchorYmd = ymdLocal(anchor);
  const dayItems: MyScheduleEntry[] = useMemo(
    () => (data?.items ?? []).filter((e) => e.work_date === anchorYmd),
    [data, anchorYmd]);
  const dayExceptions: MyScheduleException[] = useMemo(
    () => (data?.exceptions ?? []).filter((ex) => {
      const upper = ex.extends_until ?? ex.exception_date;
      return ex.exception_date <= anchorYmd && anchorYmd <= upper;
    }), [data, anchorYmd]);

  // dot under day-strip cells if there's any non-cancelled entry that day.
  const datesWithEntries = useMemo(() => {
    const s = new Set<string>();
    for (const e of data?.items ?? []) s.add(e.work_date);
    return s;
  }, [data]);

  return (
    <MobileShell title="我的排班" subtitle={session?.profile.display_name ?? ""}>
      <Card>
        <CardHeader title="未來兩週" hint={data ? `${data.from} – ${data.to}` : ""} />
        <WeekStrip anchor={anchor} onSelect={setAnchor} />
        <div className="grid grid-cols-7 gap-1.5 mt-1">
          {Array.from({ length: 7 }).map((_, i) => {
            const d0 = new Date(anchor);
            const dow = (d0.getDay() + 6) % 7;
            d0.setDate(d0.getDate() - dow + i);
            const has = datesWithEntries.has(ymdLocal(d0));
            return <div key={i} className="flex justify-center">
              {has ? <span className="h-1 w-1 rounded-full bg-accent" /> : null}
            </div>;
          })}
        </div>
      </Card>

      {dayExceptions.length > 0 && (
        <Card>
          <CardHeader title="例外日" />
          <ul className="space-y-2">
            {dayExceptions.map((ex) => (
              <li key={ex.id} className="rounded-xl bg-bg-raised p-3">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] mr-2 ${KIND_TONE[ex.kind]}`}>{KIND_LABEL[ex.kind]}</span>
                <span className="text-sm">{ex.label}</span>
                {ex.auto_skip ? <span className="ml-2 text-[10px] text-text-muted">（這天免出勤）</span> : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <CardHeader title={`${anchor.getMonth() + 1}/${anchor.getDate()} 班次`} />
        {err ? (
          <EmptyMobile title="載入失敗" body={err} />
        ) : !data ? (
          <EmptyMobile title="載入中…" />
        ) : dayItems.length === 0 ? (
          <EmptyMobile title="這天沒有排班" />
        ) : (
          <ul className="space-y-2">
            {dayItems.map((e) => (
              <li key={e.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{e.shift_name ?? "—"}</p>
                    <p className="text-[11px] text-text-muted font-mono">{e.shift_start ?? "??"} – {e.shift_end ?? "??"} · {e.site_name ?? e.site_id}</p>
                    {e.notes ? <p className="text-[11px] text-text-muted">{e.notes}</p> : null}
                  </div>
                  <span className={`text-[10px] uppercase ${e.status === "confirmed" ? "text-success" : e.status === "swapped" ? "text-warning" : "text-text-muted"}`}>{e.status}</span>
                </div>
                {e.status !== "swapped" && (
                  <div className="flex justify-end mt-2">
                    <button onClick={() => setSwapModal(e)}
                      className="text-[11px] rounded-md border border-border bg-bg-base px-2 py-1 text-text-secondary hover:text-text-primary">
                      申請換班
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {swapModal && session?.session_token && (
        <SwapRequestModal
          entry={swapModal}
          token={session.session_token}
          onClose={() => setSwapModal(null)}
          onDone={() => { setSwapModal(null); setReload((n) => n + 1); }}
        />
      )}
    </MobileShell>
  );
}

function SwapRequestModal({ entry, token, onClose, onDone }: {
  entry: MyScheduleEntry;
  token: string;
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await createSwapRequest(token, {
        requester_roster_entry_id: entry.id,
        target_officer_id: null,         // 找替班 — 由主管指派
        reason: reason.trim() || null,
      });
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "送出失敗";
      if (msg === "duplicate_pending_request") setErr("這天已經申請過換班了，請等主管處理。");
      else if (msg === "requester_entry_cancelled") setErr("這天的排班已被取消，無需換班。");
      else setErr(`送出失敗（${msg}）。`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/60 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-sm rounded-2xl bg-bg-surface border border-border p-4 space-y-3">
        <h3 className="text-base font-semibold">申請換班</h3>
        <div className="rounded-xl bg-bg-raised p-3 text-sm">
          <p className="font-medium">{entry.shift_name ?? "—"}</p>
          <p className="text-[11px] text-text-muted font-mono">
            {entry.work_date} · {entry.shift_start ?? "??"} – {entry.shift_end ?? "??"} · {entry.site_name ?? entry.site_id}
          </p>
        </div>
        <label className="block text-sm">
          <span className="text-text-secondary">原因（選填）</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-md bg-bg-base border border-border p-2 text-sm h-20"
            placeholder="例：家中急事，需要找人替班。" />
        </label>
        {err && <div className="rounded-md border border-danger/40 bg-danger/10 p-2 text-sm text-danger">{err}</div>}
        <p className="text-[11px] text-text-muted">送出後主管會收到通知，由主管指派替班人選。</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-md border border-border bg-bg-base px-3 py-1.5 text-sm" disabled={busy}>取消</button>
          <button onClick={() => void submit()} className="rounded-md bg-accent text-text-inverse px-3 py-1.5 text-sm font-medium" disabled={busy}>
            {busy ? "送出中…" : "送出申請"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface OfficerHistoryRow {
  id: string;
  scanned_at: string;             // ISO
  location_id: string | null;
  location_label: string | null;
  site_id: string | null;
  site_name: string | null;
  resolved_kind: "sdm_url" | "ntag_uid" | "location_code" | "unknown";
  verified: boolean;
  scanned_value: string | null;
}
interface OfficerHistoryStats {
  total_30d: number;
  verified_30d: number;
  unknown_30d: number;
}
interface OfficerHistoryResponse {
  items: OfficerHistoryRow[];
  stats: OfficerHistoryStats;
  next_before: string | null;
}

const SCAN_KIND_LABEL: Record<OfficerHistoryRow["resolved_kind"], string> = {
  sdm_url:         "NFC 感應",
  ntag_uid:        "UID 輸入",
  location_code: "代碼輸入",
  unknown:         "未解析",
};

/** /liff/officer/history — officer's own patrol scan history. */
export function OfficerHistoryPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<OfficerHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!session) return;
    apiFetch(
      `/v1/patrols/me/history?tenant_id=${encodeURIComponent(session.tenant_id)}&limit=50`,
      {}, { jwt: session.session_token },
    )
      .then(async (r) => {
        if (r.ok) setData(await r.json() as OfficerHistoryResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  async function loadMore(): Promise<void> {
    if (!session || !data || !data.next_before || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await apiFetch(
        `/v1/patrols/me/history?tenant_id=${encodeURIComponent(session.tenant_id)}&limit=50&before=${encodeURIComponent(data.next_before)}`,
        {}, { jwt: session.session_token },
      );
      if (r.ok) {
        const more = await r.json() as OfficerHistoryResponse;
        setData({
          items: [...data.items, ...more.items],
          stats: more.stats,
          next_before: more.next_before,
        });
      }
    } finally { setLoadingMore(false); }
  }

  if (error) return <MobileShell title="我的紀錄"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入巡邏紀錄" />;

  return (
    <MobileShell title="我的紀錄" subtitle={`近 30 天 ${data.stats.total_30d} 次掃描`}>
      <Card>
        <CardHeader title="統計（近 30 天）" />
        <div className="grid grid-cols-3 gap-2">
          {[
            { l: "總掃描", v: String(data.stats.total_30d) },
            { l: "已驗證", v: String(data.stats.verified_30d) },
            { l: "未解析", v: String(data.stats.unknown_30d) },
          ].map((m, i) => (
            <div key={i} className="rounded-xl bg-bg-raised p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">{m.l}</p>
              <p className="text-base font-semibold mt-1">{m.v}</p>
            </div>
          ))}
        </div>
      </Card>

      <MySubmissions />

      <Card>
        <CardHeader title="掃描紀錄" hint={`${data.items.length} 筆`} />
        {data.items.length === 0 ? (
          <EmptyMobile
            title="尚無掃描紀錄"
            body="完成第一次巡邏掃描後會在這顯示。"
          />
        ) : (
          <>
            <ul className="space-y-2">
              {data.items.map((it) => {
                const dt = new Date(it.scanned_at);
                const local = isNaN(dt.getTime()) ? it.scanned_at
                  : formatTaipeiDateTime(dt);
                const subline = `${local} · ${SCAN_KIND_LABEL[it.resolved_kind]}${it.verified ? " · ✓ 已驗證" : ""}`;
                return (
                  <li key={it.id}>
                    <ListRow
                      label={it.location_label ?? (it.location_id ? `(未命名 ${it.location_id.slice(0, 6)})` : "未對應地點")}
                      sublabel={`${it.site_name ?? "—"} · ${subline}`}
                      accessory={
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${it.verified ? "bg-success/15 text-success" : it.resolved_kind === "unknown" ? "bg-warning/15 text-warning" : "bg-bg-raised text-text-muted"}`}>
                          {it.verified ? "驗證" : it.resolved_kind === "unknown" ? "異常" : "已記錄"}
                        </span>
                      }
                    />
                  </li>
                );
              })}
            </ul>
            {data.next_before && (
              <div className="mt-3">
                <PrimaryButton onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "載入中…" : "載入更多"}
                </PrimaryButton>
              </div>
            )}
          </>
        )}
      </Card>

      <ComingSoonCard
        title="進階紀錄功能"
        description="統計效率指標 / 補登過期打卡 / 個人月報 PDF — 隨後續排班與報表整合上線。"
        willShow={["每日完成率", "補登過期未打卡（需主管簽核）", "效率指標（平均到站時間、跳關次數）", "個人月報下載"]}
      />

      {session && (
        <p className="text-[10px] text-text-muted text-center">
          登入 {session.profile.display_name ?? "(unknown)"} · {session.role}
        </p>
      )}
    </MobileShell>
  );
}
